import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Wrench, Plus, Search, Loader2, Car, User, ChevronRight,
  ClipboardList, FileText, Hammer, CheckCircle2, Calendar, Clock, AlertTriangle,
  Archive, ArchiveRestore, Camera, Send, SprayCan, CircleDot, Gauge, CalendarClock, Upload,
  MessageSquare, Users, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import OrImportDialog from "@/components/or/OrImportDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export const STATUT_LABELS = {
  cree: "Actif",
  pris_en_charge: "Pris en charge",
  controle: "Contrôlé (legacy)",
  devis_envoye: "Devis envoyé",
  approbation: "Devis accepté",
  commencement: "En travaux",
  travaux_effectue: "Travaux effectués",
  pret: "Prêt à récupérer",
  facture: "Prêt à récupérer (legacy)",
  paye: "Payé",
  annule: "Annulé",
  non_applicable: "Sauté (n/a)",
};

export const STATUT_COLORS = {
  cree: "bg-blue-50 text-blue-700 border-blue-200",
  pris_en_charge: "bg-cyan-50 text-cyan-700 border-cyan-200",
  controle: "bg-indigo-50 text-indigo-700 border-indigo-200",
  devis_envoye: "bg-orange-50 text-orange-700 border-orange-200",
  approbation: "bg-yellow-50 text-yellow-700 border-yellow-200",
  commencement: "bg-purple-50 text-purple-700 border-purple-200",
  travaux_effectue: "bg-green-50 text-green-700 border-green-200",
  pret: "bg-teal-50 text-teal-700 border-teal-200",
  facture: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paye: "bg-[#52a675]/15 text-[#3d8a5c] border-[#52a675]/40",
  annule: "bg-gray-100 text-gray-500 border-gray-200 line-through",
  non_applicable: "bg-gray-50 text-gray-400 border-gray-200",
};

const BUCKETS = [
  { key: "a_faire", label: "À faire", icon: ClipboardList, color: "bg-blue-50", textColor: "text-blue-700" },
  { key: "devis_envoye", label: "Devis envoyé", icon: Send, color: "bg-orange-50", textColor: "text-orange-700" },
  { key: "devis_accepte", label: "Devis accepté", icon: CheckCircle2, color: "bg-yellow-50", textColor: "text-yellow-700" },
  { key: "en_travaux", label: "En travaux", icon: Hammer, color: "bg-purple-50", textColor: "text-purple-700" },
  { key: "travaux_effectue", label: "Travaux effectués", icon: CheckCircle2, color: "bg-green-50", textColor: "text-green-700" },
];

// iter225 — Emblème + couleur par métier (carrosserie / mécanique / pneus…)
export const CATEGORIE_MO_STYLE = {
  // iter279 — Code couleur unifié avec le calendrier : vert = mécanique, bleu = carrosserie.
  carrosserie: { label: "Carrosserie", icon: SprayCan, border: "#2563eb", badge: "bg-blue-50 text-blue-700 border-blue-200" },
  peinture: { label: "Peinture", icon: SprayCan, border: "#60a5fa", badge: "bg-sky-50 text-sky-700 border-sky-200" },
  mecanique: { label: "Mécanique", icon: Wrench, border: "#52a675", badge: "bg-[#52a675]/15 text-[#2d5f3f] border-[#52a675]/40" },
  pneus: { label: "Pneus", icon: CircleDot, border: "#475569", badge: "bg-slate-100 text-slate-700 border-slate-300" },
  diagnostic: { label: "Diagnostic", icon: Gauge, border: "#7c3aed", badge: "bg-violet-50 text-violet-700 border-violet-200" },
  electrique: { label: "Électricité", icon: Gauge, border: "#ca8a04", badge: "bg-amber-50 text-amber-700 border-amber-200" },
};

// 2026-06-25 → "25.06"
const fmtDateCourt = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}.${m}`;
};

export default function AtelierDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // iter266 — Vue « Mes OR » / « Tous les OR ». Un mécano démarre sur ses OR,
  // admin et comptoir voient tout par défaut. Le choix est mémorisé par compte.
  // iter275 — Seuls les mécanos ont la bascule « Mes OR » : admin, comptoir et
  // compta ne sont pas des intervenants, ils filtrent par intervenant.
  const estIntervenant = user?.role === "atelier";
  const prefKey = user?.id ? `atelier-vue-${user.id}` : null;
  const [mesOr, setMesOr] = useState(() => {
    if (!estIntervenant) return false;
    if (prefKey) {
      const saved = localStorage.getItem(prefKey);
      if (saved !== null) return saved === "mes";
    }
    return true;
  });
  const [intervenantId, setIntervenantId] = useState("tous");
  const [mecanos, setMecanos] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState({});  // {bucket: bool}
  const [q, setQ] = useState("");
  const [showArchives, setShowArchives] = useState(false);
  // iter250 — colonne affichée en mobile (menu de sélection)
  const [mobileBucket, setMobileBucket] = useState("a_faire");
  const [importSource, setImportSource] = useState(null); // 'auto-i' | 'guido' | null

  // iter266 — Intervenant appliqué au filtre serveur : « Mes OR » gagne sur le
  // sélecteur, sinon le sélecteur (réservé admin/comptoir) ou rien.
  // iter273 — « me » : le serveur résout la fiche intervenant du compte connecté
  // (les OR sont attribués à l'intervenant, pas au compte utilisateur).
  const filtreIntervenant = (estIntervenant && mesOr)
    ? "me"
    : (intervenantId !== "tous" ? intervenantId : undefined);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/atelier/dashboard", {
        params: filtreIntervenant
          // iter290 — en vue « Mes OR », les dossiers que personne n'a pris
          // restent visibles pour que chacun puisse se les attribuer.
          ? { intervenant_id: filtreIntervenant, ...(estIntervenant && mesOr ? { inclure_non_attribues: true } : {}) }
          : {},
      });
      // iter100f — Nouvelle réponse: {buckets, counts, has_more, limit}.
      // Fallback compat: ancienne réponse plate {bucket: [...]}.
      if (resp && resp.buckets) {
        setData(resp);
      } else {
        setData({ buckets: resp, counts: {}, has_more: {}, limit: 10 });
      }
    } finally {
      setLoading(false);
    }
  }, [filtreIntervenant, estIntervenant, mesOr]);

  const loadMore = useCallback(async (bucket) => {
    setLoadingMore((m) => ({ ...m, [bucket]: true }));
    try {
      const current = data?.buckets?.[bucket] || [];
      const offset = current.filter((it) => !it.is_rdv).length;  // ne pas compter les RDV virtuels
      const { data: resp } = await api.get(`/atelier/dashboard/bucket/${bucket}`, {
        params: {
          offset, limit: 10,
          ...(filtreIntervenant ? { intervenant_id: filtreIntervenant } : {}),
          ...(estIntervenant && mesOr ? { inclure_non_attribues: true } : {}),
        },
      });
      setData((d) => ({
        ...d,
        buckets: { ...d.buckets, [bucket]: [...current, ...(resp.items || [])] },
        counts: { ...d.counts, [bucket]: resp.total },
        has_more: { ...d.has_more, [bucket]: resp.has_more },
      }));
    } finally {
      setLoadingMore((m) => ({ ...m, [bucket]: false }));
    }
  }, [data, filtreIntervenant, estIntervenant, mesOr]);

  // iter290 — « Je le prends » depuis la carte : attribution immédiate au mécano connecté
  const [prendEnCours, setPrendEnCours] = useState(null);
  const prendreOr = async (or) => {
    setPrendEnCours(or.id);
    try {
      await api.post(`/or/${or.id}/prendre`);
      toast.success(`OR ${or.numero} vous est attribué`);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attribution impossible");
    } finally {
      setPrendEnCours(null);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (estIntervenant) return;   // le mécano n'a pas besoin de la liste
    api.get("/atelier/mecaniciens").then(({ data: d }) => setMecanos(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => setMecanos([]));
  }, [estIntervenant]);

  const toggleVue = () => {
    setMesOr((v) => {
      if (prefKey) localStorage.setItem(prefKey, v ? "tous" : "mes");
      return !v;
    });
    setIntervenantId("tous");
  };

  if (loading) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!data) return null;

  const filterCard = (or) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (or.numero || "").toLowerCase().includes(s) ||
           (or.client_nom || "").toLowerCase().includes(s) ||
           (or.vehicule_label || "").toLowerCase().includes(s);
  };

  // Helper rendering a card (OR existant ou RDV planifié virtuel)
  const renderCard = (or) => {
    if (or.is_rdv) {
      // Carte RDV : on propose la création d'OR avec préremplissage
      return (
        <button
          key={or.id}
          onClick={() => navigate("/atelier/or/nouveau", {
            state: {
              rdvId: or.rdv_id,
              client: { id: or.client_id, nom: or.client_nom },
              vehiculeId: or.vehicule_id || "",
              // iter349 — la note prise au rendez-vous suit dans la demande du client
              notesClient: or.rdv_notes || or.notes || "",
            },
          })}
          className="w-full text-left bg-white p-2.5 rounded border-l-4 border border-gray-200 hover:shadow-md hover:border-l-[#52a675] transition-all"
          style={{ borderLeftColor: or.rdv_type_couleur }}
          data-testid={`rdv-card-${or.rdv_id}`}
          title="Démarrer l'OR depuis ce RDV"
        >
          <div className="flex items-center justify-between mb-1">
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[9px]">
              <Calendar className="w-2.5 h-2.5 mr-1" /> RDV
            </Badge>
            {or.rdv_urgence && (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-[9px]">
                <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> URG
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />
            <span className="font-semibold text-gray-700">{or.rdv_heure}</span>
            <span className="text-gray-400">·</span>
            <span className="truncate">{or.rdv_type_label}</span>
          </div>
          <div className="text-xs font-semibold text-gray-900 flex items-center gap-1 mt-1.5">
            <User className="w-3 h-3 text-gray-400" />
            <span className="truncate">{or.client_nom}</span>
          </div>
          {or.vehicule_label && (
            <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
              <Car className="w-3 h-3" />
              <span className="truncate">{or.vehicule_label}</span>
            </div>
          )}
          {or.reservation_label && (
            <div className="text-[10px] mt-1 px-1.5 py-0.5 bg-[#52a675]/10 text-[#2d5f3f] rounded inline-flex items-center gap-0.5 font-semibold" title={`Véhicule de remplacement : ${or.reservation_label}`}>
              🚗 {or.reservation_label.split('·')[1]?.trim() || or.reservation_label}
            </div>
          )}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <div className="text-[10px] text-gray-400 italic">Cliquez pour démarrer l'OR</div>
            <ChevronRight className="w-3 h-3 text-[#52a675]" />
          </div>
        </button>
      );
    }
    const cat = CATEGORIE_MO_STYLE[or.categorie_dominante];
    const CatIcon = cat?.icon;
    return (
      <div
        key={or.id}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/atelier/or/${or.id}`)}
        onKeyDown={(e) => { if (e.key === "Enter") navigate(`/atelier/or/${or.id}`); }}
        className={`w-full text-left bg-white p-2.5 rounded border border-gray-200 border-l-4 hover:shadow-md hover:border-[#52a675]/30 transition-all cursor-pointer ${
          or.non_attribue ? "ring-1 ring-amber-300" : ""
        }`}
        style={{ borderLeftColor: cat?.border || "#e5e7eb" }}
        data-testid={`or-card-${or.id}`}
      >
        <div className="flex items-center justify-between mb-1">
          <code className="text-[11px] font-bold text-gray-700 bg-gray-50 px-1.5 py-0.5 rounded">{or.numero}</code>
          <div className="flex items-center gap-1">
            {or.messages_non_lus > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-[9px] font-bold text-white bg-red-600 rounded-full px-1.5 py-0.5"
                title={`${or.messages_non_lus} message(s) client non lu(s)`}
                data-testid={`or-card-${or.id}-unread`}
              >
                <MessageSquare className="w-2.5 h-2.5" /> {or.messages_non_lus}
              </span>
            )}
            <Badge variant="outline" className={`${STATUT_COLORS[or.statut]} text-[9px]`}>{STATUT_LABELS[or.statut]}</Badge>
          </div>
        </div>
        {cat && (
          <Badge variant="outline" className={`${cat.badge} text-[9px] mb-1`} data-testid={`or-card-${or.id}-categorie`}>
            <CatIcon className="w-2.5 h-2.5 mr-1" /> {cat.label}
          </Badge>
        )}
        <div className="text-xs font-semibold text-gray-900 flex items-center gap-1 mt-1">
          <User className="w-3 h-3 text-gray-400" />
          <span className="truncate">{or.client_nom}</span>
        </div>
        {or.vehicule_label && (
          <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
            <Car className="w-3 h-3" />
            <span className="truncate">{or.vehicule_label}</span>
          </div>
        )}
        {or.rdv_planifie_date && (
          <div className="text-[10px] mt-1 px-1.5 py-0.5 bg-[#52a675]/10 text-[#2d5f3f] rounded inline-flex items-center gap-1 font-semibold" data-testid={`or-card-${or.id}-rdv`}>
            <CalendarClock className="w-2.5 h-2.5" />
            RDV {fmtDateCourt(or.rdv_planifie_date)} · {or.rdv_planifie_heure}
          </div>
        )}
        {or.devis_complement_en_attente && (
          <div className="text-[10px] mt-1 px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded inline-flex items-center gap-1 font-semibold">
            <AlertTriangle className="w-2.5 h-2.5" /> Complément v{or.devis_revision} en attente
          </div>
        )}
        {or.intervenant_nom && (
          <div className="text-[10px] text-gray-500 flex items-center gap-1 mt-0.5" data-testid={`or-card-${or.id}-intervenant`}>
            <UserCheck className="w-3 h-3 text-[#52a675]" />
            <span className="truncate">{or.intervenant_nom}</span>
          </div>
        )}
        {/* iter290 — Dossier que personne n'a pris : visible par tous, attribuable en un clic */}
        {or.non_attribue && (
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`or-card-${or.id}-non-attribue`}>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-semibold inline-flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> Non attribué
            </span>
            {estIntervenant && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prendreOr(or); }}
                disabled={prendEnCours === or.id}
                className="text-[10px] px-2 py-0.5 rounded bg-[#52a675] text-white font-semibold hover:bg-[#3d8a5c] disabled:opacity-60 inline-flex items-center gap-1"
                data-testid={`or-card-${or.id}-prendre`}
              >
                {prendEnCours === or.id
                  ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                  : <UserCheck className="w-2.5 h-2.5" />}
                Je le prends
              </button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="text-[10px] text-gray-500">{or.lignes_count} ligne{or.lignes_count > 1 ? "s" : ""}</div>
          <div className="text-xs font-mono font-bold text-gray-900">
            {or.totaux?.total_ttc?.toFixed(2)} CHF
          </div>
        </div>
      </div>
    );
  };

  // iter141 — Les cartes RDV virtuelles ne sont plus affichées dans le dashboard
  // atelier. La création d'OR se fait via QR code depuis l'interface mobile de
  // prise en charge sur place (/atelier/checkin). L'agenda reste la source des RDV.
  const isRealOr = (or) => !or?.is_rdv;
  const totalAll = BUCKETS.reduce(
    (sum, b) => sum + (data.buckets?.[b.key]?.filter(isRealOr).length || 0),
    0,
  );

  const renderBucket = (b) => {
            // iter141 — Exclut les cartes RDV virtuelles (création OR via QR code)
            const items = (data.buckets?.[b.key] || []).filter(isRealOr).filter(filterCard);
            const totalInBucket = data.counts?.[b.key] ?? items.length;
            const hasMore = !!data.has_more?.[b.key];
            const Icon = b.icon;
            return (
              <div key={b.key} className="flex flex-col" data-testid={`bucket-${b.key}`}>
                <div className={`p-3 rounded-t-md ${b.color} border border-b-0 border-gray-200`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${b.textColor}`} />
                    <h3 className={`font-heading font-semibold text-sm ${b.textColor}`}>{b.label}</h3>
                    <Badge variant="outline" className="ml-auto bg-white text-xs" data-testid={`bucket-${b.key}-count`}>
                      {items.length}{totalInBucket > items.length ? ` / ${totalInBucket}` : ""}
                    </Badge>
                  </div>
                  {(data.masques_planifies?.[b.key] || 0) > 0 && (
                    <div className="mt-1.5 text-[10px] text-gray-600 flex items-center gap-1" data-testid={`bucket-${b.key}-planifies`}>
                      <CalendarClock className="w-2.5 h-2.5" />
                      {data.masques_planifies[b.key]} planifié{data.masques_planifies[b.key] > 1 ? "s" : ""} plus tard (agenda)
                    </div>
                  )}
                </div>
                <div className="flex-1 bg-gray-50/50 border border-t-0 border-gray-200 rounded-b-md p-2 space-y-2 min-h-[200px]">
                  {items.length === 0 && (
                    <div className="text-xs text-gray-300 text-center py-6">Vide</div>
                  )}
                  {items.map((or) => renderCard(or))}
                  {/* iter100f — Bouton « +10 de plus » : visible quand il reste des OR à charger */}
                  {hasMore && !q && (
                    <button
                      onClick={() => loadMore(b.key)}
                      disabled={!!loadingMore[b.key]}
                      className="w-full text-xs py-2 mt-1 rounded border-2 border-dashed border-gray-300 text-gray-600 hover:border-[#52a675] hover:text-[#52a675] hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-wait"
                      data-testid={`bucket-${b.key}-load-more`}
                    >
                      {loadingMore[b.key] ? (
                        <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Chargement…</span>
                      ) : (
                        <>+ {Math.min(10, totalInBucket - items.length)} de plus ({items.length}/{totalInBucket})</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
  };

  return (
    <div data-testid="atelier-dashboard">
      {/* iter250 — Barre d'actions harmonisée : une seule action primaire (Nouvel OR),
          les imports regroupés dans un menu, le reste en boutons neutres.
          Sur mobile les libellés secondaires disparaissent au profit des icônes. */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un OR, un client, une plaque…"
            className="pl-10 h-10"
            data-testid="or-search"
          />
        </div>

        {estIntervenant && (
        <Button
          variant={mesOr ? "default" : "outline"}
          onClick={toggleVue}
          className={`h-10 flex-shrink-0 ${mesOr ? "bg-[#52a675] hover:bg-[#3d8a5c]" : "border-gray-300 text-gray-600"}`}
          title={mesOr ? "Vous voyez vos OR — cliquez pour voir tous les OR" : "Vous voyez tous les OR — cliquez pour voir uniquement les vôtres"}
          data-testid="or-vue-toggle"
        >
          {mesOr ? <UserCheck className="w-4 h-4 sm:mr-2" /> : <Users className="w-4 h-4 sm:mr-2" />}
          <span className="hidden sm:inline">{mesOr ? "Mes OR" : "Tous les OR"}</span>
        </Button>
        )}

        {!estIntervenant && (
          <Select value={intervenantId} onValueChange={setIntervenantId}>
            <SelectTrigger className="h-10 flex-1 min-w-[140px] text-xs sm:text-sm sm:flex-none sm:w-[185px] sm:flex-shrink-0" data-testid="or-intervenant-filter">
              <SelectValue placeholder="Intervenant" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tous" data-testid="or-intervenant-option-tous">Tous les intervenants</SelectItem>
              {/* iter292 — repérer d'un coup les dossiers que personne n'a pris */}
              <SelectItem value="__aucun__" data-testid="or-intervenant-option-aucun">Sans intervenant</SelectItem>
              {mecanos.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`or-intervenant-option-${m.id}`}>{m.label || m.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Button
          variant="outline" size="icon"
          onClick={() => setShowArchives(true)}
          className="h-10 w-10 border-gray-300 text-gray-600 sm:w-auto sm:px-3"
          title="Consulter les OR archivés"
          data-testid="or-archives-btn"
        >
          <Archive className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Archives</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline" size="icon"
              className="h-10 w-10 border-gray-300 text-gray-600 sm:w-auto sm:px-3"
              title="Importer un OR depuis un rapport Auto-I ou des captures Guido"
              data-testid="or-import-menu-btn"
            >
              <Upload className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Importer</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => setImportSource("auto-i")} data-testid="or-import-autoi-btn">
              <FileText className="w-4 h-4 mr-2 text-purple-600" /> Rapport Auto-I (PDF)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setImportSource("guido")} data-testid="or-import-guido-btn">
              <Camera className="w-4 h-4 mr-2 text-teal-600" /> Captures Guido
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="outline" size="icon"
          onClick={() => navigate("/atelier/or-parc/nouveau")}
          className="h-10 w-10 border-gray-300 text-gray-600 sm:w-auto sm:px-3"
          title="Nouvel OR pour un véhicule du parc"
          data-testid="or-parc-new-button"
        >
          <Car className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">OR Parc</span>
        </Button>

        <Button
          onClick={() => navigate("/atelier/or/nouveau")}
          className="h-10 bg-[#52a675] hover:bg-[#3d8a5c] flex-shrink-0"
          data-testid="or-new-button"
        >
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Nouvel OR</span>
        </Button>
      </div>

      {totalAll === 0 ? (
        <Card className="p-12 text-center border-dashed border-2 border-gray-200">
          <Wrench className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="font-heading font-semibold text-lg text-gray-700">Aucun ordre de réparation</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Démarrez en créant un OR depuis ici, ou depuis la fiche client / un RDV au calendrier.
          </p>
          <Button onClick={() => navigate("/atelier/or/nouveau")} className="bg-[#52a675] hover:bg-[#3d8a5c]">
            <Plus className="w-4 h-4 mr-2" /> Créer le premier OR
          </Button>
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-3">Le workflow se déroulera dans ces 5 étapes :</p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {BUCKETS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={b.key} className="flex items-center gap-1">
                    <div className={`px-2.5 py-1 rounded-md ${b.color} flex items-center gap-1.5`}>
                      <Icon className={`w-3.5 h-3.5 ${b.textColor}`} />
                      <span className={`text-xs font-semibold ${b.textColor}`}>{b.label}</span>
                    </div>
                    {i < BUCKETS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* iter250 — Mobile : une seule colonne à la fois, choisie dans un menu
              (les 5 étapes empilées faisaient une page interminable). */}
          <div className="md:hidden mb-3">
            <Select value={mobileBucket} onValueChange={setMobileBucket}>
              <SelectTrigger className="h-10" data-testid="bucket-mobile-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUCKETS.map((b) => {
                  const n = (data.counts?.[b.key] ?? (data.buckets?.[b.key] || []).filter(isRealOr).length);
                  return (
                    <SelectItem key={b.key} value={b.key} data-testid={`bucket-mobile-option-${b.key}`}>
                      {b.label} ({n})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="md:hidden">
            {renderBucket(BUCKETS.find((b) => b.key === mobileBucket) || BUCKETS[0])}
          </div>

          <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-5 gap-3">
            {BUCKETS.map(renderBucket)}
          </div>
        </>
      )}
      {/* Dialog Archives — liste les OR archivés (statut métier préservé) */}
      <ArchivesDialog open={showArchives} onClose={() => setShowArchives(false)} navigate={navigate} onChanged={fetchData} />

      {/* iter167 — Dialog d'import OR (Auto-I PDF / Guido screenshots) */}
      {importSource && (
        <OrImportDialog
          open={!!importSource}
          onClose={() => setImportSource(null)}
          source={importSource}
        />
      )}
    </div>
  );
}

function ArchivesDialog({ open, onClose, navigate, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const fetchArchives = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archive: true, limit: 200 };
      if (q) params.q = q;
      const { data } = await api.get("/or", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    if (open) fetchArchives();
  }, [open, fetchArchives]);

  const desarchive = async (oid) => {
    try {
      await api.post(`/or/${oid}/desarchive`);
      toast.success("OR désarchivé");
      fetchArchives();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="archives-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-gray-500" /> Archives — OR retirés du flux actif
          </DialogTitle>
          <DialogDescription>
            Les OR archivés sont retirés des vues atelier principales mais restent accessibles ici.
            Aucune donnée n'est supprimée — historique complet conservé.
          </DialogDescription>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par n° OR, client, plaque…"
            className="pl-10"
            data-testid="archives-search"
          />
        </div>
        {loading ? (
          <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-500" data-testid="archives-empty">
            Aucun OR archivé.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">OR</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Véhicule</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">TTC</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Archivé le</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((or) => (
                  <tr key={or.id} className="border-t border-gray-100 hover:bg-gray-50/60" data-testid={`archive-row-${or.numero}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-xs">{or.numero}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className={STATUT_COLORS[or.statut]}>{STATUT_LABELS[or.statut]}</Badge></td>
                    <td className="px-3 py-2 text-gray-800 text-xs">{or.client_nom}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{or.vehicule_label}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{or.totaux?.total_ttc?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">{or.archive_at?.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => { onClose(); navigate(`/atelier/or/${or.id}`); }} className="mr-1" data-testid={`archive-open-${or.numero}`}>
                        Ouvrir <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => desarchive(or.id)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`archive-restore-${or.numero}`}>
                        <ArchiveRestore className="w-3 h-3 mr-1" /> Restaurer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
